import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { MaquinaParaDragDrop } from '../../../services/asignacionesUsuarioMaquinaService';

// ===============================
// TIPOS
// ===============================

interface DraggableMachineProps {
  maquina: MaquinaParaDragDrop;
  isInPool?: boolean; // Si está en el pool de disponibles
  sourceUserId?: number; // ID del usuario si está asignada
}

// ===============================
// COMPONENTE
// ===============================

export function DraggableMachine({ maquina, isInPool = false, sourceUserId }: DraggableMachineProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `machine-${maquina.id}`,
    data: {
      type: 'machine',
      maquina,
      sourceUserId,
      isFromPool: isInPool,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  // Icono según tipo de máquina
  const getMachineIcon = (tipo: string) => {
    switch (tipo.toUpperCase()) {
      case 'EXCAVADORA':
        return '🚜';
      case 'BULLDOZER':
        return '🚧';
      case 'GRUA':
        return '🏗️';
      case 'CAMION':
        return '🚛';
      case 'TRACTOR':
        return '🚜';
      case 'CARGADOR':
        return '⚡';
      case 'MOTONIVELADORA':
        return '🛤️';
      case 'COMPACTADORA':
        return '🔄';
      case 'HARVESTER':
        return '🌲';
      default:
        return '🏭';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        group relative bg-white rounded-lg border-2 shadow-sm p-3 cursor-grab active:cursor-grabbing
        transition-all duration-200 hover:shadow-md
        ${isDragging 
          ? 'border-blue-400 shadow-lg scale-105 z-50' 
          : isInPool 
            ? 'border-gray-200 hover:border-blue-300' 
            : 'border-green-200 hover:border-green-300'
        }
        ${isDragging ? 'opacity-50' : 'opacity-100'}
      `}
    >
      {/* Indicador de dragging */}
      {isDragging && (
        <div className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
          ↗
        </div>
      )}

      {/* Contenido de la tarjeta */}
      <div className="flex items-center space-x-3">
        {/* Icono de máquina */}
        <div className="text-2xl">
          {getMachineIcon(maquina.tipo)}
        </div>

        {/* Información */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-gray-900 truncate">
              {maquina.codigo}
            </p>
            <span className={`
              inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
              ${isInPool 
                ? 'bg-gray-100 text-gray-800' 
                : 'bg-green-100 text-green-800'
              }
            `}>
              {maquina.tipo}
            </span>
          </div>
          
          <p className="text-xs text-gray-500 truncate mt-1">
            {maquina.descripcion}
          </p>
          
          <div className="flex items-center space-x-2 mt-1">
            <span className="text-xs text-gray-400">
              {maquina.marca} {maquina.modelo}
            </span>
          </div>
        </div>

        {/* Handle visual para drag */}
        <div className="opacity-40 group-hover:opacity-100 transition-opacity">
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
          </svg>
        </div>
      </div>

      {/* Hover effect */}
      <div className="absolute inset-0 rounded-lg bg-blue-50 opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none"></div>
    </div>
  );
}
